import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  matchRoutes,
  createRoutesFromChildren,
} from 'react-router-dom';

// Core Sentry init is synchronous so error capture and performance tracing
// start immediately. Session Replay is deferred until the browser is idle —
// it instruments DOM mutations and adds 200-400ms to boot if loaded inline,
// which we want to avoid on iOS PWA cold start.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      matchRoutes,
      createRoutesFromChildren,
    }),
  ],

  // Drop benign noise that is not an actionable fault. The Supabase auth
  // client coordinates session refresh across tabs with the Web Locks API.
  // When one tab forcibly takes over the lock (the 'steal' option), the
  // waiting tab's promise rejects with this AbortError and self-corrects.
  // It is most common in Edge/PWA with several tabs open and breaks nothing.
  ignoreErrors: [
    "Lock broken by another request with the 'steal' option",
  ],

  // Performance: capture 20% of transactions in production
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

  // Session Replay sample rates apply once the integration loads (below).
  // Record only sessions that hit an error — normal sessions are not worth
  // a replay and were burning the monthly allowance with nothing to watch.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Only send errors from our domains
  allowUrls: [
    /cleanmybed\.com/,
    /netlify\.app/,
  ],
});

// Defer Replay until the browser is idle (after first paint). 3s timeout
// ensures it loads even on long-running pages where idle never fires.
const loadReplay = () => {
  Sentry.addIntegration(Sentry.replayIntegration());
};

if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(loadReplay, { timeout: 3000 });
  } else {
    setTimeout(loadReplay, 1500);
  }
}
